import { Box, Text } from '@chakra-ui/react';
import { GetIngestionDocumentFragment } from '@client/graphql/__generated__/types';
import { FC, useCallback } from 'react';
import { CenterSpinner } from '~/common/components/CenterSpinner';
import { PDFScrollable } from '~/services/document/components/PDFScrollable/PDFScrollable';

interface CompliancePDFViewerProps {
  activeFieldId: string | string[];
  selectedDocumentLoading: boolean;
  selectedContractDocumentId: string | null;
  selectedDocument: GetIngestionDocumentFragment | null;
  validationFieldsMap: Map<
    number,
    Array<{
      id: string;
      x: number;
      y: number;
      width: number;
      height: number;
      pageNumber: number;
      contractDocumentUuid: string;
    }>
  >;
}

export const CompliancePDFViewer: FC<CompliancePDFViewerProps> = ({
  activeFieldId,
  selectedContractDocumentId,
  selectedDocument,
  selectedDocumentLoading,
  validationFieldsMap,
}) => {
  const ValidationFieldPageChildren = useCallback(
    ({
      page,
    }: {
      page: { pageNumber: number; height: number; width: number };
    }) => {
      const fields = validationFieldsMap.get(page.pageNumber) || [];
      const pageHeight = page.height;
      const pageWidth = page.width;

      return (
        <>
          {fields
            .filter(({ id, contractDocumentUuid }) => {
              const fieldMatches = Array.isArray(activeFieldId)
                ? activeFieldId.includes(id)
                : activeFieldId === id;
              if (selectedContractDocumentId && fieldMatches) {
                return contractDocumentUuid === selectedContractDocumentId;
              }

              return fieldMatches;
            })
            .map((field) => (
              <Box
                key={field.id}
                bgColor="compliance.pdf.highlight"
                borderWidth="0px"
                data-preview-field-id={field.id}
                data-preview-page-number={page.pageNumber}
                pointerEvents="none"
                position="absolute"
                style={{
                  height: `${field.height * pageHeight}px`,
                  left: `${field.x * pageWidth}px`,
                  top: `${field.y * pageHeight}px`,
                  width: `${field.width * pageWidth}px`,
                }}
              />
            ))}
        </>
      );
    },
    [validationFieldsMap, activeFieldId, selectedContractDocumentId]
  );

  if (selectedDocumentLoading) {
    return (
      <Box
        alignItems="center"
        display="flex"
        h="full"
        justifyContent="center"
        p={6}
      >
        <CenterSpinner />
      </Box>
    );
  } else if (!selectedDocument) {
    return (
      <Box
        alignItems="center"
        display="flex"
        h="full"
        justifyContent="center"
        p={6}
      >
        <Text>
          Select an ingestion document from the sidebar to view the PDF here.
        </Text>
      </Box>
    );
  }

  return (
    <Box
      bg="compliance.pdf.bg"
      display="flex"
      flexDirection="column"
      height="100%"
      position="relative"
      px={4}
      width="100%"
    >
      <Box borderBottomWidth={1} flexShrink={0} p={2}>
        <Text fontSize="md" fontWeight="semibold" mb={1}>
          {selectedDocument.documentName}
        </Text>
      </Box>

      <Box flex={1} minHeight={0} width="100%">
        <PDFScrollable
          isDraggable
          defaultZoom={1}
          maxWidth={window.innerWidth}
          pageChildren={ValidationFieldPageChildren}
          pdfFiles={[
            {
              name: selectedDocument.documentName,
              url:
                selectedDocument.contractDocuments?.[0]?.file?.url ||
                selectedDocument.fileUrl ||
                '',
            },
          ]}
        />
      </Box>
    </Box>
  );
};
